'use client';
import { ButtonProps } from '@/components/Button/Button';
import { Modal } from '@/components/Modal/Modal';
import Table, { PartialRowData } from '@/components/Table/Table';
import PlusCircle from '@/components/icons/PlusCircle';
import { NotifsContext } from '@/contexts';
import useNavigate from '@/hooks/useNavigate';
import {
  DeletePDVreq,
  useDeletePdvMutation,
  useExportPdvsMutation,
  useLazyGetListPdvQuery,
} from '@/services/API';
import { useAppSelector } from '@/store/hooks';
import { INumeroAgrement } from '@/types';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useContext, useEffect, useRef, useState } from 'react';

type NumAgrTableData = Pick<
  INumeroAgrement,
  'bnppf_pos_id' | 'name' | 'partner_pos_id' | 'id'
> &
  PartialRowData;

export default function Page() {
  const selectedSte = useAppSelector((state) => state.selectedSte.societe);
  const [selectedNumAgr, setSelectedNumAgr] = useState<NumAgrTableData[]>([]);
  const [tableData, setTableData] = useState<NumAgrTableData[]>([]);
  const [openedModal, setOpenedModal] = useState(false);
  const [getListPdv, { data: pdvsSource, isLoading: isLoadingData }] =
    useLazyGetListPdvQuery();

  const [suprPdvs, { isLoading: isLoadingDelete }] = useDeletePdvMutation();
  const [exportPdvs, { isLoading: isExporting }] = useExportPdvsMutation();
  const navigate = useNavigate();

  const edit = (row: NumAgrTableData) => {
    navigate('numero-agrement/modifier/?id=' + row.id);
  };

  useEffect(() => {
    if (!pdvsSource) return;
    setTableData(
      pdvsSource.map<NumAgrTableData>(
        ({ bnppf_pos_id, name, partner_pos_id, id }) => ({
          bnppf_pos_id,
          name,
          partner_pos_id,
          id,
        })
      )
    );
  }, [pdvsSource]);

  useEffect(() => {
    getListPdv(selectedSte.siren);
  }, []);

  async function deletePDV() {
    if (selectedNumAgr.length) {
      const data = selectedNumAgr.map<DeletePDVreq>(({ id }) => ({ id }));

      let req = { siren: selectedSte.siren, resources: data };
      suprPdvs(req)
        .unwrap()
        .then(() => {
          addToast &&
            addToast({
              toastType: 'success',
              children: 'Confirmation de suppression.',
            });
          getListPdv(selectedSte.siren);
        })
        .catch(() => {
          addToast &&
            addToast({
              toastType: 'error',
              children: `Une erreur est survenue.
            Rééssayer ultérieurement.`,
            });
        })
        .finally(() => setOpenedModal(false));
    }
  }

  const columnHelper = createColumnHelper<NumAgrTableData>();
  const cols = [
    columnHelper.accessor('bnppf_pos_id', {
      header: () => "Numéro d'agrément",
      meta: { filterVariant: 'text' },
    }),
    columnHelper.accessor('name', {
      header: () => 'Libellé',
      meta: { filterVariant: 'text' },
    }),
    columnHelper.accessor('partner_pos_id', {
      header: () => 'Identifiant magasin partenaire',
    }),
  ] as ColumnDef<NumAgrTableData>[];

  const { addToast } = useContext(NotifsContext);
  const hiddenLink = useRef<HTMLAnchorElement>(null);

  function onExport() {
    exportPdvs()
      .unwrap()
      .then((data) => {
        const file = new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), data], {
          type: 'text/csv',
        });
        if (hiddenLink.current) {
          hiddenLink.current.href = URL.createObjectURL(file);
          hiddenLink.current.click();
        }
        addToast &&
          addToast({
            toastType: 'success',
            children: "Confirmation de l'export.",
          });
      })
      .catch(() => {
        addToast &&
          addToast({
            toastType: 'error',
            children: `Une erreur est survenue.
        Rééssayer ultérieurement.`,
          });
      });
  }

  const leftbtns: ButtonProps[] = [
    {
      variant: 'secondary',
      children: 'Importer',
      onClick: () => navigate('numero-agrement/importer/'),
    },
    {
      variant: 'secondary',
      disabled: !pdvsSource || pdvsSource.length === 0,
      children: 'Exporter',
      loading: isExporting,
      onClick: onExport,
    },
  ];

  const rightbtns: ButtonProps[] = [
    {
      disabled: selectedNumAgr.length === 0,
      onClick: () => setOpenedModal(true),
      variant: 'secondary',
      children: 'Supprimer',
    },
    {
      onClick: () => navigate('numero-agrement/creer'),
      icon: <PlusCircle />,
      children: 'Ajouter',
    },
  ];

  return (
    <>
      {/* @ts-ignore */}
      <Table<NumAgrTableData>
        title="Liste des numéros d'agrément"
        leftbtns={leftbtns}
        rightbtns={rightbtns}
        onEdit={edit}
        columns={cols}
        columnsSort={[{ id: 'name', desc: false }]}
        data={tableData}
        enableFilter={true}
        onSelectionChange={setSelectedNumAgr}
        isLoadingData={isLoadingData}
      />
      <Modal
        primaryBtnProps={{
          loading: isLoadingDelete,
          children: 'Confirmer',
          onClick: deletePDV,
        }}
        secondaryBtnProps={{
          children: 'Annuler',
          onClick: () => setOpenedModal(false),
        }}
        title="Voulez-vous supprimer le numéro d’agrément ?"
        open={openedModal}
        onClose={() => setOpenedModal(false)}
      />
      <a
        ref={hiddenLink}
        download="export_agréments.csv"
        style={{ display: 'none' }} // Make the file input element invisible
      />
    </>
  );
}
